import { SelectFieldCP } from 'modules/fields/components/select-field/SelectFieldCP'
import { SelectFieldRegulationCP } from 'common/components/form/SelectFieldRegulationCP'
import { SelectNatureCP } from 'common/components/form/SelectNatureCP'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { InputCP } from 'submodules/nerit-framework-ui/common/components/form-fields/input/InputCP'
import { BasicModalCPProps, ModalCP } from 'submodules/nerit-framework-ui/common/components/modal/ModalCP'
import { useRequest } from 'submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest'
import { FieldRegulationRequests } from 'submodules/nerit-aquatec-sdk/services/regulation/FieldRegulationRequests'
import { FieldRegulationResponseDTO } from 'submodules/nerit-aquatec-sdk/services/regulation/dtos/responses/FieldRegulationResponseDTO'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { useFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager'
import { FieldRegulationFormModel } from 'modules/fields/components/modals/modal-field-regulation/inner/FieldRegulationFormModel'
import { FormStateManagerUtils } from 'submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils'
import { FieldRegulationSaveRequestDTO } from 'submodules/nerit-aquatec-sdk/services/regulation/dtos/requests/FieldRegulationSaveRequestDTO'
import { RequestUtils } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { ConditionalLimitFormCP } from 'modules/fields/components/modals/modal-field-regulation/inner/ConditionalLimitFormCP'
import { ConditionalStateTP, FieldRegulationModalUtils } from 'modules/fields/components/modals/modal-field-regulation/inner/FieldRegulationModalUtils'

interface ICPProps extends BasicModalCPProps {
    fieldRegulation?: FieldRegulationResponseDTO
}

/**
 */
export function ModalFieldRegulationCP(props: ICPProps): JSX.Element {

    const [formModel, setFormModel] = useState<FieldRegulationFormModel>(new FieldRegulationFormModel())
    const formStateManager = useFormStateManager(formModel)

    const [conditionalState, setConditionalState] = useState<ConditionalStateTP>(FieldRegulationModalUtils.initialConditionalState())
    const [showConditionalErrors, setShowConditionalErrors] = useState<boolean>(false)

    const saveRequest = useRequest<void>('none')
    useEffect(onSaveRequestChange, [saveRequest.isAwaiting])

    useEffect(init, [props.visible])

    /**
     */
    function init(): void {

        setFormModel(new FieldRegulationFormModel())
        setConditionalState(FieldRegulationModalUtils.initialConditionalState(props.visible ? props.fieldRegulation : undefined))
        setShowConditionalErrors(false)
        if (!props.visible || !props.fieldRegulation)
            return

        setFormModel(new FieldRegulationFormModel({
            fieldCode: props.fieldRegulation.field.code,
            regulationCode: props.fieldRegulation.regulation.code,
            maxPermitted: props.fieldRegulation.maxPermitted,
            minPermitted: props.fieldRegulation.minPermitted,
            nature: props.fieldRegulation.nature
        }))
    }

    async function save(): Promise<void> {

        if (!await FormStateManagerUtils.validateRequiredFields(formStateManager))
            return

        if (conditionalState.isConditional && !FieldRegulationModalUtils.isConditionalComplete(conditionalState)) {
            setShowConditionalErrors(true)
            NotificationHelper.error('Ops', 'Preencha os campos obrigatórios')
            return
        }

        const formValues = formStateManager.getFormValues()!
        const dto: FieldRegulationSaveRequestDTO = FieldRegulationModalUtils.toSaveDTO(formValues, conditionalState)

        if (!!props.fieldRegulation)
            saveRequest.runRequest(FieldRegulationRequests.update(props.fieldRegulation.code, dto))
        else
            saveRequest.runRequest(FieldRegulationRequests.save(dto))
    }

    /**
     * Fixo e condicional se excluem: ao trocar, os valores do outro modo são descartados.
     */
    function toggleConditional(): void {
        formStateManager.changeFieldValue('minPermitted', undefined)
        formStateManager.changeFieldValue('maxPermitted', undefined)
        setShowConditionalErrors(false)
        setConditionalState(FieldRegulationModalUtils.toggle(conditionalState))
    }

    /**
     */
    function onSaveRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(saveRequest, NotificationHelper.DEFAULT_ERROR_SAVE_MESSAGE, NotificationHelper.DEFAULT_SUCCESS_SAVE_MESSAGE, true))
            return

        props.onSave()
    }

    return (
        <ModalCP
            visible={props.visible}
            actionLoading={saveRequest.isAwaiting}
            onOk={save}
            onCancel={props.onCancel}
            title={'Nova Norma'}
            width={500}
        >
            <SelectFieldCP
                isMany={false}
                isRequired={true}
                formStateManager={formStateManager}
                fieldName={'fieldCode'}
            />

            <ConditionalLinkSCP>
                <span role={'button'} tabIndex={0} onClick={toggleConditional}>{FieldRegulationModalUtils.linkText(conditionalState)}</span>
            </ConditionalLinkSCP>

            <SelectFieldRegulationCP
                isRequired={true}
                formStateManager={formStateManager}
                fieldName={'regulationCode'}
            />

            <SelectNatureCP
                isRequired={true}
                formStateManager={formStateManager}
                fieldName={'nature'}
            />

            {
                conditionalState.isConditional
                    ? <ConditionalLimitFormCP
                        state={conditionalState}
                        onChange={setConditionalState}
                        fieldCode={formStateManager.getFieldValue('fieldCode')}
                        showErrors={showConditionalErrors}
                    />
                    : <RowCP>
                        <ColumnCP size={12}>
                            <InputCP
                                label={'Mínimo Permitido'}
                                formStateManager={formStateManager}
                                fieldName={'minPermitted'}
                            />
                        </ColumnCP>
                        <ColumnCP size={12}>
                            <InputCP
                                label={'Máximo Permitido'}
                                formStateManager={formStateManager}
                                fieldName={'maxPermitted'}
                            />
                        </ColumnCP>
                    </RowCP>
            }
        </ModalCP>
    )
}

const ConditionalLinkSCP = styled.div`
    text-align: right;
    margin-top: 4px;

    span {
        color: ${ThemeProject.primary};
        text-decoration: underline;
        cursor: pointer;
    }
`
